/**
 * 音声操作列 (`AudioControls`) の共通ヘルパー (SBI-009)。
 *
 * 純関数と型だけを置く。コンポーネントと同じファイルから export すると
 * `react-refresh/only-export-components` の警告になるため分けている。
 */
import type { SsmlAudioState } from './useSsmlAudio';

/** 音声の生成中 (順番待ちを含む) か。 */
export function isAudioSynthesizing(audio: SsmlAudioState | undefined): boolean {
  return audio?.status === 'pending' || audio?.status === 'synthesizing';
}

/**
 * 再生できる音声の URL (無ければ `null`)。
 *
 * ⚠️ `status === 'ready'` だけでなく URL の有無も見る。プレイヤーとダウンロードが
 * 同じ値を見るため、「聴いた音」と「保存した音」が食い違わない。
 */
export function getReadyAudioUrl(audio: SsmlAudioState | undefined): string | null {
  return audio?.status === 'ready' ? audio.url : null;
}

/**
 * 現在の入力が、音声を作った時点の入力から編集されているか。
 *
 * ⚠️ 前後の空白差は「編集」と見なさない (改行だけで警告が出ると無視され、
 * 本当に必要なときに読まれなくなる)。`applied` が無い (音声の元が不明) 場合は `false`。
 */
export function hasEditedSinceApplied(
  current: string,
  applied: string | null | undefined,
): boolean {
  return applied !== null && applied !== undefined && current.trim() !== applied.trim();
}

/** `data-testid` の一覧 (呼び出し側が Step ごと / 候補ごとに決める)。 */
export interface AudioControlsTestIds {
  /** 操作列全体 */
  root: string;
  /** `<audio>` プレイヤー */
  player: string;
  /** 音声が無い間の状態表示 (未作成 / 生成中) */
  status: string;
  /**
   * 生成中だけ `status` の代わりに使う状態表示の testid (省略時は常に `status`)。
   * Step3 は「生成中」と「未作成」を別の testid で区別している。
   */
  pending?: string;
  /** 再作成ボタン */
  recreate: string;
  /** ダウンロード (リンク / 無効ボタン共通) */
  download: string;
  /** 「編集内容は音声に反映されていません」 */
  dirty: string;
  /** 音声合成の失敗メッセージ */
  error: string;
}

/** Step1 / Step2 形式 (`${prefix}-audio` など) の testid を作る。 */
export function buildStepAudioTestIds(prefix: string): AudioControlsTestIds {
  return {
    root: `${prefix}-audio`,
    player: `${prefix}-audio-player`,
    status: `${prefix}-audio-status`,
    recreate: `${prefix}-recreate`,
    download: `${prefix}-download`,
    dirty: `${prefix}-dirty`,
    error: `${prefix}-audio-error`,
  };
}
